import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ShoppingCart, Minus, Plus, FileText, Truck, Invoice } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatPrice, tierPrice } from "../lib/api";
import { useCart } from "../context/CartContext";
import { ProductCard } from "../components/ProductCard";

export default function ProductPage() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [qty, setQty] = useState(1);
  const { addItem } = useCart();
  const navigate = useNavigate();

  useEffect(() => {
    setProduct(null);
    setNotFound(false);
    setQty(1);
    api.get(`/products/${slug}`).then(({ data }) => setProduct(data)).catch(() => setNotFound(true));
    window.scrollTo(0, 0);
  }, [slug]);

  if (notFound) return <p className="py-24 text-center text-slate-500">Produkts nav atrasts.</p>;
  if (!product) return <div className="mx-auto max-w-7xl px-4 py-12"><div className="h-96 bg-[#F4F5F7] animate-pulse" /></div>;

  const unit = tierPrice(product.price_tiers, qty);
  const lineTotal = unit * qty;

  return (
    <div className="mx-auto max-w-7xl px-4 py-8">
      <nav className="text-xs text-slate-500">
        <Link to="/" className="hover:text-[#2E86D1]">Sākums</Link> / <Link to={`/katalogs?category=${product.category}`} className="hover:text-[#2E86D1]">Katalogs</Link> / <span>{product.sku}</span>
      </nav>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-10">
        <div className="border border-border bg-[#F4F5F7]">
          <img src={product.image} alt={product.name} className="aspect-square w-full object-cover" />
        </div>

        <div>
          <p className="text-xs font-bold tracking-[0.2em] uppercase text-slate-500">{product.brand} · Art. {product.sku}</p>
          <h1 data-testid="product-title" className="mt-2 font-heading text-2xl md:text-3xl font-black tracking-tight">{product.name}</h1>
          <p className={`mt-3 text-sm font-semibold ${product.stock > 0 ? "text-[#16A34A]" : "text-[#2E86D1]"}`}>
            {product.stock > 0 ? `● Noliktavā: ${product.stock} gab` : "● Nav noliktavā"}
          </p>

          <div className="mt-6 rounded-2xl overflow-hidden border border-slate-300">
            <p className="bg-[#2E86D1] px-4 py-2.5 text-xs font-bold tracking-[0.2em] uppercase text-white">Apjoma cenas (bez PVN)</p>
            <table className="w-full text-sm" data-testid="tier-price-table">
              <tbody>
                {product.price_tiers.map((t, i) => {
                  const active = unit === t.price && qty >= t.min_qty;
                  return (
                    <tr key={i} className={`border-b border-border last:border-b-0 ${active ? "bg-blue-50" : ""}`}>
                      <td className="px-4 py-2.5 font-medium">{t.min_qty === 1 ? "No 1 gab" : `No ${t.min_qty} gab`}</td>
                      <td className="px-4 py-2.5 text-right font-bold font-heading">{formatPrice(t.price)}</td>
                      <td className="px-4 py-2.5 text-right text-xs text-slate-500 w-24">
                        {i > 0 && <span className="text-[#16A34A] font-bold">-{Math.round((1 - t.price / product.price_tiers[0].price) * 100)}%</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="mt-6 flex flex-wrap items-center gap-4">
            <div className="flex items-center border border-slate-300">
              <button data-testid="qty-minus-btn" aria-label="Samazināt" onClick={() => setQty(Math.max(1, qty - 1))} className="btn-industrial h-12 w-12 flex items-center justify-center hover:bg-[#2E86D1] hover:text-white hover:border-[#2E86D1]"><Minus size={16} /></button>
              <input data-testid="qty-input" type="number" min="1" value={qty}
                onChange={(e) => setQty(Math.max(1, parseInt(e.target.value) || 1))}
                className="h-12 w-16 text-center text-sm font-bold focus:outline-none" />
              <button data-testid="qty-plus-btn" aria-label="Palielināt" onClick={() => setQty(qty + 1)} className="btn-industrial h-12 w-12 flex items-center justify-center hover:bg-[#2E86D1] hover:text-white hover:border-[#2E86D1]"><Plus size={16} /></button>
            </div>
            <div>
              <p data-testid="line-total" className="font-heading text-2xl font-black">{formatPrice(lineTotal)}</p>
              <p className="text-xs text-slate-500">{formatPrice(unit)}/gab bez PVN · ar PVN {formatPrice(lineTotal * 1.21)}</p>
            </div>
          </div>

          <div className="mt-6 flex flex-wrap gap-3">
            <button data-testid="product-add-to-cart-btn"
              onClick={() => { addItem(product, qty); toast.success(`Pievienots grozam: ${qty} gab`, { description: product.name }); }}
              className="btn-industrial inline-flex items-center gap-2 bg-[#2E86D1] px-8 py-3.5 text-sm font-semibold tracking-wide text-white hover:bg-[#1F6DB2]">
              <ShoppingCart size={18} /> Pievienot grozam
            </button>
            <button data-testid="product-quote-btn"
              onClick={() => navigate("/piedavajums", { state: { item: { sku: product.sku, name: product.name, qty } } })}
              className="btn-industrial inline-flex items-center gap-2 border border-slate-300 px-6 py-3.5 text-sm font-semibold tracking-wide hover:bg-[#2E86D1] hover:text-white hover:border-[#2E86D1]">
              <FileText size={18} /> Pieprasīt piedāvājumu
            </button>
          </div>

          <div className="mt-8 grid grid-cols-2 gap-4 text-xs text-slate-600">
            <div className="flex items-center gap-2 border border-border p-3"><Truck size={20} className="text-[#2E86D1]" /> Piegāde visā Latvijā 1–2 darba dienās</div>
            <div className="flex items-center gap-2 border border-border p-3"><Invoice size={20} className="text-[#2E86D1]" /> B2B apmaksa ar rēķinu</div>
          </div>
        </div>
      </div>

      <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <h2 className="font-heading text-xl font-semibold tracking-tight">Apraksts</h2>
          <p className="mt-3 text-sm leading-relaxed text-slate-700">{product.description}</p>
        </div>
        <div>
          <h2 className="font-heading text-xl font-semibold tracking-tight">Specifikācija</h2>
          <table className="mt-3 w-full text-sm border border-border">
            <tbody>
              {Object.entries(product.specs || {}).map(([k, v]) => (
                <tr key={k} className="border-b border-border last:border-b-0">
                  <td className="px-4 py-2 font-semibold bg-[#F4F5F7] w-1/3">{k}</td>
                  <td className="px-4 py-2">{v}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {product.related?.length > 0 && (
        <div className="mt-14">
          <h2 className="font-heading text-xl font-semibold tracking-tight">Līdzīgi produkti</h2>
          <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-4">
            {product.related.map((p) => <ProductCard key={p.sku} product={p} />)}
          </div>
        </div>
      )}
    </div>
  );
}
